import { createContext, useContext, useEffect, useMemo } from 'react'
import type { ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'
import type { MeContext, ModuleInfo, NavItem, Permission, PermissionScope, User } from '@/lib/types'
import { useAuth } from './AuthProvider'
import { Splash } from './Splash'

export const ME_CONTEXT_QUERY_KEY = ['me', 'context'] as const

interface MeContextValue {
  user: User
  firmName: string
  modules: ModuleInfo[]
  moduleKeys: string[]
  permissions: Record<string, Permission>
  /** decision === 'yes' | 'conditional' (UI nie egzekwuje RBAC — to robi backend). */
  can: (permissionKey: string) => boolean
  /** Zakres uprawnienia: 'all' | 'team' | 'own' | null. */
  scopeOf: (permissionKey: string) => PermissionScope | null
  /** Wszystkie pozycje nawigacji ze wszystkich widocznych modułów. */
  navItems: (NavItem & { moduleKey: string; moduleName: string })[]
  hasModule: (key: string) => boolean
  refetch: () => void
}

const Context = createContext<MeContextValue | null>(null)

/** Ładuje /api/me/context — jedyne źródło nawigacji i uprawnień (CONTRACTS.md §3). */
export function MeContextProvider({ children }: { children: ReactNode }) {
  const { setUser } = useAuth()

  const query = useQuery({
    queryKey: ME_CONTEXT_QUERY_KEY,
    queryFn: () => api.get<MeContext>('/me/context'),
    staleTime: 5 * 60_000,
    retry: 1,
  })

  const data = query.data

  useEffect(() => {
    if (data?.user) setUser(data.user)
  }, [data?.user, setUser])

  const value = useMemo<MeContextValue | null>(() => {
    if (!data) return null
    const modules = data.modules ?? []
    const permissions = data.permissions ?? {}
    return {
      user: data.user,
      firmName: data.firm_name ?? 'ActaModus',
      modules,
      moduleKeys: modules.map((m) => m.key),
      permissions,
      can: (key) => {
        const permission = permissions[key]
        return Boolean(permission && permission.decision !== 'no')
      },
      scopeOf: (key) => permissions[key]?.scope ?? null,
      navItems: modules.flatMap((module) =>
        (module.nav ?? []).map((item) => ({ ...item, moduleKey: module.key, moduleName: module.name })),
      ),
      hasModule: (key) => modules.some((m) => m.key === key),
      refetch: () => void query.refetch(),
    }
  }, [data, query])

  if (query.isPending) return <Splash label="Wczytywanie kontekstu…" />

  if (query.isError || !value) {
    return (
      <Splash
        label="Nie udało się pobrać kontekstu użytkownika."
        detail="Sprawdź, czy backend odpowiada na /api/me/context."
        onRetry={() => void query.refetch()}
      />
    )
  }

  return <Context.Provider value={value}>{children}</Context.Provider>
}

/** Kontekst zalogowanego użytkownika: uprawnienia, moduły, nawigacja. */
export function useMe(): MeContextValue {
  const context = useContext(Context)
  if (!context) throw new Error('useMe musi być użyty wewnątrz <MeContextProvider>')
  return context
}
